import {useEffect} from 'react'
import {AgendaEventAbsence, AgendaEventAppointment, AgendaEventUnavailability} from '@/features/agenda/types'
import {useAgendaCalendarStore} from '@/features/agenda/stores/agendaCalendarStore'
import {useShallow} from 'zustand/react/shallow'
import {useDateValidation} from '@/features/agenda/hooks/useDateValidation'

/**
 * Custom hook to handle calendar data
 * @param appointments - The appointments to display
 * @param blockouts - The blockouts to display
 * @returns The filtered appointments, blockouts and check functions
 */
interface UseCalendarDataProps {
    appointments: AgendaEventAppointment[]
    blockouts: AgendaEventAbsence[]
    unavailabilities: AgendaEventUnavailability[]
}

export const useCalendarData = ({appointments, blockouts, unavailabilities}: UseCalendarDataProps) => {
    const {isHoliday, isDisabledDay} = useDateValidation()

    const {
        appointments: storedAppointments,
        blockouts: storedBlockouts,
        unavailabilities: storedUnavailabilities,
        setAppointments,
        setBlockouts,
        setUnavailabilities,
        resetCalendar
    } = useAgendaCalendarStore(
        useShallow(state => ({
            appointments: state.appointments,
            blockouts: state.blockouts,
            unavailabilities: state.unavailabilities,
            setAppointments: state.setAppointments,
            setBlockouts: state.setBlockouts,
            setUnavailabilities: state.setUnavailabilities,
            resetCalendar: state.resetCalendar
        }))
    )

    // Sync appointments and blockouts with the store
    useEffect(() => {
        setAppointments(appointments)
    }, [appointments, setAppointments])

    useEffect(() => {
        setBlockouts(blockouts)
    }, [blockouts, setBlockouts])

    useEffect(() => {
        setUnavailabilities(unavailabilities)
    }, [unavailabilities, setUnavailabilities])

    useEffect(() => {
        return () => resetCalendar()
    }, [])

    return {
        storedAppointments,
        storedBlockouts,
        storedUnavailabilities,
        isDisabledDay,
        isHoliday
    }
}
